import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Colors,
    Heading,
    Label,
    SelectFilled,
    Text,
    TextInput,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useId } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { CareerStepDateFields } from './CareerStepDateFields';
import { CareerStepFormField } from './CareerStepFormField';
import {
    careerStepKindLabelKey,
    careerStepSchema,
    CAREER_STEP_TITLE_MAX_LENGTH,
    isOpenEndedKind,
    isTitleRequired,
} from './careerStepValidation';
import { GROWTH_CAPTION, GROWTH_SELECT_BORDER } from './growthStyles';
import { readPositionName } from './targetPosition';

import type { CareerStepFormValues } from './careerStepValidation';
import type { CareerStep, CareerStepKind } from '@/types/career-growth';
import type { OptionProps } from '@amelio/ui-library';

import { useGetCompetencyProfiles } from '@/api/competency-services/competency-profile';
import { useAmelioTranslations } from '@/hooks';
import { CAREER_STEP_KINDS } from '@/types/career-growth';
import { getLocalizedText } from '@/utils/localization';
import { validationMessageKey } from '@/utils/validationMessage';

interface CareerStepFormProps {
    /** "New step" or "Edit step": the form announces which of the two it is. */
    heading: string;
    submitLabel: string;
    defaultValues: CareerStepFormValues;
    /** The profile assigned to the employee today, shown read-only on a "current position". */
    currentProfile: { id: string; name: string } | null;
    /**
     * The steps that already hold a unique kind (at most one "current", one "target"): their kind
     * stays listed, named by the step holding it, but cannot be picked twice.
     */
    stepByTakenKind: Partial<Record<CareerStepKind, CareerStep>>;
    isSaving: boolean;
    onSubmit: (values: CareerStepFormValues) => void;
    onCancel: () => void;
    /**
     * Where the form sits. `'card'` is the new step, which stands on its own at the head of the
     * list; `'inline'` is the step being edited, whose form is the lower half of that step's card.
     * @default 'card'
     */
    surface?: CareerStepFormSurface;
    dataTestId: string;
}

export type CareerStepFormSurface = 'card' | 'inline';

/** A `SelectFilled` value is a string, so "no profile" travels as the empty option. */
const NO_PROFILE_VALUE = '';

// The form is always the active thing on screen, so it answers in the accent colour either way:
// outlined and raised when it stands alone, and separated by a single rule when it continues the
// card of the step it edits.
const SURFACE_CLASSES: Record<CareerStepFormSurface, string> = {
    card: 'border-primary/50 rounded-xl border bg-white p-4 shadow-sm',
    inline: 'border-primary/30 border-t px-4 pt-4 pb-4',
};

/**
 * The form of a single career step, used both for the new step at the head of the list and for the
 * step being edited in place. It owns its own `<form>`, so Enter saves; the parent only decides
 * which step it edits and what happens on save.
 */
export const CareerStepForm = ({
    heading,
    submitLabel,
    defaultValues,
    currentProfile,
    stepByTakenKind,
    isSaving,
    onSubmit,
    onCancel,
    surface = 'card',
    dataTestId,
}: CareerStepFormProps) => {
    const { t } = useAmelioTranslations();
    const fieldId = useId();

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<CareerStepFormValues>({
        resolver: zodResolver(careerStepSchema),
        defaultValues,
    });

    const kind = watch('kind');
    const startDate = watch('startDate');
    const isCurrent = kind === 'Current';
    const isEndDateDisabled = isOpenEndedKind(kind);
    const isTitleMandatory = isTitleRequired({
        kind,
        competencyProfileId: watch('competencyProfileId'),
    });

    // Only a past step is asked for a competency profile of its own; the current position mirrors
    // the profile the employee is assigned today, and a target position picks one from the list.
    const { data: profiles } = useGetCompetencyProfiles(!isCurrent);

    // A step that is still running has no end date, and a current position is always linked to the
    // assigned profile: changing the kind rewrites both instead of leaving a stale value behind.
    useEffect(() => {
        if (isEndDateDisabled) {
            setValue('endDate', null, { shouldValidate: true });
        }
        if (isCurrent) {
            setValue('competencyProfileId', currentProfile?.id ?? null);
        }
    }, [isCurrent, isEndDateDisabled, currentProfile?.id, setValue]);

    const kindOptions: OptionProps[] = CAREER_STEP_KINDS.map((candidate) => {
        const takenBy = candidate === defaultValues.kind ? undefined : stepByTakenKind[candidate];
        return {
            value: candidate,
            label: t(careerStepKindLabelKey(candidate)),
            // Named by the step already holding it, so a greyed-out choice says why it is greyed.
            description: takenBy && readPositionName(takenBy),
            disabled: !!takenBy,
        };
    });

    const profileOptions: OptionProps[] = [
        { value: NO_PROFILE_VALUE, label: t('performanceProfile.growth.stepsEditor.noProfile') },
        ...(profiles ?? []).map((profile) => ({
            value: profile.id,
            label: getLocalizedText(profile.name),
        })),
    ];

    const errorMessage = (message?: string, options?: Record<string, unknown>) =>
        message ? t(validationMessageKey(message), options) : undefined;

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className={`flex flex-col gap-4 ${SURFACE_CLASSES[surface]}`}
            data-testid={dataTestId}
        >
            <Heading
                level={6}
                color={Colors.NAVY}
            >
                {heading}
            </Heading>

            <CareerStepFormField
                label={
                    <Label
                        htmlFor={`${fieldId}-title`}
                        color={Colors.NAVY}
                        required={isTitleMandatory}
                    >
                        {t('performanceProfile.growth.stepsEditor.positionTitle')}
                    </Label>
                }
            >
                <Controller
                    name={'title'}
                    control={control}
                    render={({ field }) => (
                        <TextInput
                            id={`${fieldId}-title`}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            placeholder={t(
                                'performanceProfile.growth.stepsEditor.positionPlaceholder',
                            )}
                            maxLength={CAREER_STEP_TITLE_MAX_LENGTH}
                            variant={'white'}
                            bordered
                            required={isTitleMandatory}
                            error={!!errors.title}
                            errorMessage={errorMessage(errors.title?.message, {
                                max: CAREER_STEP_TITLE_MAX_LENGTH,
                            })}
                            dataTestId={`${dataTestId}-title`}
                        />
                    )}
                />
            </CareerStepFormField>

            <CareerStepFormField
                label={
                    <Label
                        htmlFor={`${fieldId}-kind`}
                        color={Colors.NAVY}
                    >
                        {t('performanceProfile.growth.stepsEditor.type')}
                    </Label>
                }
            >
                <Controller
                    name={'kind'}
                    control={control}
                    render={({ field }) => (
                        <SelectFilled
                            id={`${fieldId}-kind`}
                            options={kindOptions}
                            value={field.value}
                            onChange={(value) => field.onChange(value as CareerStepKind)}
                            sort={'none'}
                            variant={'white'}
                            triggerBorderClassName={GROWTH_SELECT_BORDER}
                            ariaLabel={t('performanceProfile.growth.stepsEditor.type')}
                            dataTestId={`${dataTestId}-kind`}
                        />
                    )}
                />
            </CareerStepFormField>

            <CareerStepDateFields
                control={control}
                kind={kind}
                startDate={startDate}
                startDateError={errorMessage(errors.startDate?.message)}
                endDateError={errorMessage(errors.endDate?.message)}
                dataTestId={dataTestId}
            />

            {isCurrent ? (
                <CareerStepFormField
                    label={
                        <Label
                            htmlFor={`${fieldId}-current-profile`}
                            color={Colors.NAVY}
                        >
                            {t('performanceProfile.growth.stepsEditor.competencyProfile')}
                        </Label>
                    }
                >
                    <TextInput
                        id={`${fieldId}-current-profile`}
                        value={
                            currentProfile?.name ||
                            t('performanceProfile.growth.stepsEditor.noAssignedProfile')
                        }
                        readOnly
                        variant={'white'}
                        dataTestId={`${dataTestId}-current-profile`}
                    />
                    <span className={GROWTH_CAPTION}>
                        {t('performanceProfile.growth.stepsEditor.profileLinkedToCurrent')}
                    </span>
                </CareerStepFormField>
            ) : (
                <CareerStepFormField
                    label={
                        <Text
                            size={'small'}
                            color={Colors.NAVY}
                            weight={'medium'}
                        >
                            {t('performanceProfile.growth.stepsEditor.profileOptional')}
                        </Text>
                    }
                >
                    <Controller
                        name={'competencyProfileId'}
                        control={control}
                        render={({ field }) => (
                            <SelectFilled
                                options={profileOptions}
                                value={field.value ?? NO_PROFILE_VALUE}
                                onChange={(value) => field.onChange(value || null)}
                                searchable
                                searchPlaceholder={t(
                                    'performanceProfile.growth.stepsEditor.searchProfile',
                                )}
                                placeholder={t(
                                    'performanceProfile.growth.stepsEditor.profilePlaceholder',
                                )}
                                variant={'white'}
                                triggerBorderClassName={GROWTH_SELECT_BORDER}
                                ariaLabel={t(
                                    'performanceProfile.growth.stepsEditor.profileOptional',
                                )}
                                dataTestId={`${dataTestId}-profile`}
                            />
                        )}
                    />
                </CareerStepFormField>
            )}

            <div className={'flex justify-end gap-2'}>
                <ButtonSecondary
                    size={'sm'}
                    onClick={onCancel}
                    dataTestId={`${dataTestId}-cancel`}
                >
                    {t('common.cancel')}
                </ButtonSecondary>
                <ButtonGradientPrimary
                    size={'sm'}
                    type={'submit'}
                    loading={isSaving}
                    dataTestId={`${dataTestId}-submit`}
                >
                    {submitLabel}
                </ButtonGradientPrimary>
            </div>
        </form>
    );
};
